import EcoSpaceSmall from 'eco-design/base/eco-space-small';
import EcoTitle from 'eco-design/base/eco-title';
import EcoIconSwiperArrow from 'eco-design-micro/icons/eco-icon-swiper-arrow';
import EcoImg from 'eco-design/base/eco-img';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper';
import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Swiper as SwiperType } from 'swiper/types';
import { FormatMoneyLocal } from 'eco-design-micro/price/FormatMoneyLocal';
import EcoLink from 'eco-design/base/eco-link';
import _ from 'lodash';
import EfIcon from '@elem/ui/ef-icon';
import { useProductStore } from '@elem/compPage/rapid-mag-power-bank/ProductProvider';
import { useProductDispatch } from '../../../../ProductProvider';
import styles from './index.module.scss';
import data from './data';
import useProductInfo from '@/utils/hooks/useProductInfo';
import { useLang } from '@/i18n';
import { useScheduledContent } from '@/utils/hooks/useScheduledContent';
import gtm from '@/utils/gtm';
import getLocal from '@/app/utils/getLocal';

type GTM_TYPE = 'Left' | 'Right';

// 颜色映射对象
const colorMap = {
    black: '#212121',
    silver: '#cdcdcd',
    blue: '#c0defe',
    white: '#e2e2df',
    purple: '#b5b2e3',
    green: '#a6cba1',
    pink: '#f3caa8',
};

const PowerSolution: React.FC = () => {
    const { t } = useLang('theProductV2.PowerSolution');
    const { locale = 'jp' } = getLocal();
    const { pageStatus } = useProductStore();
    const dispatch = useProductDispatch();

    /** 初始数据 */
    const initData = data.jp.map((i) => (i.show ? (pageStatus === i.show ? i : { ...i, children: undefined }) : i));
    /** 选中状态 */
    const [active, setActive] = useState(0);
    const [activeColor, setActiveColor] = useState(data.jp.map(() => 0));
    const [swiperInstance, setSwiperInstance] = useState<SwiperType>();
    const [type, setType] = useState(initData[0].type);

    const isBuy = useScheduledContent({
       '2025/12/26 11:00:00 GMT+8': true,
       '2025/12/09 11:00:00 GMT+8': true,
       '2025/12/12 11:00:00 GMT+8': false,
    })
    /** 产品数据 */
    const list = useProductInfo(initData, {
        variantImg: 'pc',
    } as const);
    useEffect(() => {
        dispatch({
            type: 'changeProductInfo',
            payload: list.filter((i) => (i.show ? i.show === pageStatus : i)).filter((i) => i.spec),
        });
    }, [list, dispatch, pageStatus]);
    const productList = list
        .map((i, idx) => ({
            ...i,
            children: i.children,
            title: t(`item${idx + 1}`),
            lBtn: i.show === pageStatus && !isBuy ? t('presale'):t('buy'),
            lBtn2: t('buy_disabled'),
            lHref: `https://${locale}.ecoflow.com/products/${i?.handle}?variant=${i?.variantId}`,
            rBtn: i.show === pageStatus && !isBuy ? t('presaleAmazon') : t('amazon'),
            value1_name: t(`name${idx + 1}_1`),
            value2_name: t(`name${idx + 1}_2`),
            value3_name: t(`name${idx + 1}_3`),
            value1: t(`value${idx + 1}_1`),
            value2: t(`value${idx + 1}_2`),
            value3: t(`value${idx + 1}_3`),
        }))
        .filter((i) => i.children && i.children.length > 0) as any[];
    const typeList = Object.entries(_.groupBy(productList, 'type'));

    /** 当前图片信息（用于展示） */
    const currentProduct = useMemo(() => productList[active], [productList, active]);

    /** 颜色切换 */
    const handleColorSelect = (e: MouseEvent<HTMLSpanElement>, productIdx: number, colorIdx: number) => {
        e.stopPropagation();
        const newActiveColor = [...activeColor];
        newActiveColor[productIdx] = colorIdx;
        setActiveColor(newActiveColor);
        const width = document.documentElement.clientWidth;
        if (width <= 1024) return;
        swiperInstance?.slideTo(colorIdx, 0, false);
    };

    /** 监听当前产品变化,切换产品动画 */
    const animRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (animRef.current) {
            // 1. 先删除动画 class（重置）
            animRef.current.classList.remove('fade-in');
            // 2. 触发重绘，让浏览器认为 class 被真正移除了
            void animRef.current?.offsetWidth;
            // 3. 重新加上动画 class
            animRef.current?.classList.add('fade-in');
        }

        if (swiperInstance) {
            swiperInstance.slideTo(activeColor[active] || 0, 0); // 回到初始位置
            swiperInstance.update(); // 刷新 Swiper 尺寸和状态
        }
    }, [active]);

    const handleGTM = (type: GTM_TYPE) => {
        let obj = {};
        const suffix = type.toLowerCase();
        switch (type) {
            case 'Right':
            case 'Left':
                obj = {
                    event: 'uaEvent',
                    event_name: 'product_detail_page',
                    list_name: t('title'),
                    button_name: `${type} switch`,

                    __btn_id1: `PowerSolution .swiper-container .s-${suffix}`,
                    __btn_id2: `s-${suffix}`,
                    __btn_type: 'common',
                };
                break;
        }
        gtm.push(obj);
    };

    const getProductsLink = (item: (typeof productList)[0], k: number) => {
        const product = item.children[activeColor[k]];
        return `https://${locale}.ecoflow.com/products/${product?.handle}?variant=${product?.variantId}`;
    };

    return (
        <section
            className={`PowerSolution ${styles.comp}  GtmModuleData-section`}
            id="PowerSolution"
            data-section-id="PowerSolution"
            data-section-title={t('title')}
        >
            <EcoTitle title={t('title')} />
            <EcoSpaceSmall />
            <div className="swiper-container">
                <Swiper
                    className="box-swiper"
                    slidesPerView={'auto'}
                    spaceBetween={24}
                    speed={0}
                    navigation={{
                        nextEl: '.eco-swiper-button-next',
                        prevEl: '.eco-swiper-button-prev',
                    }}
                    modules={[Navigation]}
                    onTransitionStart={(swiper) => {
                        setActiveColor((state) => {
                            const newState = [...state];
                            newState[active] = swiper.activeIndex;
                            return newState;
                        });
                    }}
                    onSwiper={(swiper) => {
                        setSwiperInstance(swiper);
                    }}
                    initialSlide={activeColor[active]}
                >
                    {productList?.[active]?.children?.map((i: any, index: number) => {
                        return (
                            <SwiperSlide key={index} className={`box-item`}>
                                <div className="inner">
                                    <div className="img-box">
                                        <EcoImg {...i} />
                                    </div>
                                </div>
                            </SwiperSlide>
                        );
                    })}
                    {productList[active].children.length > 1 && (
                        <div className="eco-swiper-control">
                            <div className="eco-swiper-navigation">
                                <div className="GtmModuleData-btn-common eco-swiper-button-prev s-left" data-btn-id="s-left" onClick={() => handleGTM('Left')}>
                                    <EcoIconSwiperArrow className="eco-swiper-button-icon" />
                                </div>
                                <div
                                    className="GtmModuleData-btn-common eco-swiper-button-next s-right"
                                    data-btn-id="s-right"
                                    onClick={() => handleGTM('Right')}
                                >
                                    <EcoIconSwiperArrow className="eco-swiper-button-icon" />
                                </div>
                            </div>
                        </div>
                    )}
                </Swiper>
                <div className="swiper-info" ref={animRef}>
                    {(currentProduct.value1_name || currentProduct.value1) && (
                        <div className="info-item info-item-1">
                            <div className="item-value">{currentProduct.value1}</div>
                            <div className="item-name">{currentProduct.value1_name}</div>
                        </div>
                    )}
                    {(currentProduct.value2_name || currentProduct.value2) && (
                        <div className="info-item info-item-2">
                            <div className="item-value">{currentProduct.value2}</div>
                            <div className="item-name">{currentProduct.value2_name}</div>
                        </div>
                    )}
                    {(currentProduct.value3_name || currentProduct.value3) && (
                        <div className="info-item info-item-3">
                            <div className="item-value">{currentProduct.value3}</div>
                            <div className="item-name">{currentProduct.value3_name}</div>
                        </div>
                    )}
                </div>
            </div>
            <div className="product-type">
                {typeList.map((item, k) => (
                    <div className={`product-type-item ${type === item[0] ? 'active' : ''}`} key={k} style={{ flex: item[1].length }}>
                        <div className="product-type-item-title">{t(item[0])}</div>
                    </div>
                ))}
            </div>
            <div className="product-cards">
                {productList.map((item, k) => {
                    const data = item.children[activeColor[k]];
                    const linkUrl = item.show === pageStatus && !isBuy?`https://${locale}.ecoflow.com/products/${data?.handle}?variant=${data?.variantId}`:`https://${locale}.ecoflow.com/cart/${data.variantId}:1?channel=buy_button`
                    return (
                        <div
                            key={k}
                            className={`GtmModuleData-btn-common product-cards-item ${k === active ? 'active' : ''} product-item-${k}`}
                            onMouseEnter={() => {
                                setActive(k);
                                setType(item.type);
                            }}
                            data-btn-id={`product-item-${k}`}
                            onClick={() => {
                                if (data.lDisabled) return;
                                window.open(getProductsLink(item, k), '_blank');
                                gtm.push({
                                    event: 'uaEvent',
                                    event_name: 'buy',
                                    product_name: item.title,
                                    position: `Collections_${t('title')}`,

                                    __btn_id1: `PowerSolution .product-item-${k + 1}`,
                                    __btn_id2: `product-item-${k}`,
                                    __btn_type: 'common',
                                });
                            }}
                        >
                            <div className="product-img">
                                <EcoImg pc={data?.pc} />
                            </div>
                            <div className="product-new">{item.new ? t('new') : ''}</div>
                            <ul className="product-colors">
                                {item?.children?.length > 1 &&
                                    item.children?.map((color: any, m: number) => {
                                        return (
                                            <li
                                                className={`GtmModuleData-btn-common item-color ${activeColor[k] === m ? ' active' : ''} color-${m}`}
                                                data-btn-id={`product-color-${k}-${m}`}
                                                onClick={(e) => {
                                                    handleColorSelect(e, k, m);
                                                    gtm.push({
                                                        event: 'uaEvent',
                                                        event_name: 'product_detail_page',
                                                        list_name: t('title'),
                                                        button_name: `${item.title}_${color.color || color?.name}`,

                                                        __btn_id1: `.product-item-${k} .color-${m}`,
                                                        __btn_id2: `product-color-${k}-${m}`,
                                                        __btn_type: 'common',
                                                    });
                                                }}
                                                style={{ '--item-color': colorMap[color.name as 'black'] }}
                                                key={`${k}_${m}`}
                                            ></li>
                                        );
                                    })}
                            </ul>
                            <div className="product-title">{item.title}</div>
                            <div className="product-footer">
                                <div className="product-price">
                                    {item.children[activeColor[k]]?.price && (
                                        <div className="price">{(FormatMoneyLocal(data?.price, locale) || '').replace(/(\d)(€)/, '$1 $2')}</div>
                                    )}
                                    {data?.compareAtPrice && data?.compareAtPrice > data?.price && (
                                        <div className="discount">{(FormatMoneyLocal(data?.compareAtPrice, locale) || '').replace(/(\d)(€)/, '$1 $2')}</div>
                                    )}
                                </div>
                                <div className="product-btns">
                                    <EcoLink
                                        className={`GtmModuleData-btn-common product-btn-buy btn-buy ${data?.lDisabled ? 'disabled' : ''}`}
                                        href={data?.lDisabled ? '' : linkUrl}
                                        data-btn-id={`product-btn-buy-${k}`}
                                        dataLayer={{
                                            event: 'uaEvent',
                                            event_name: 'checkout',
                                            button_name: `${data?.lDisabled ? item?.lBtn2 : item?.lBtn}_${item.title}`,

                                            __btn_id1: `product-item-${k} .btn-buy`,
                                            __btn_id2: `product-btn-buy-${k}`,
                                            __btn_type: 'common',
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {data?.lDisabled ? item?.lBtn2 : item?.lBtn}
                                    </EcoLink>
                                    {data?.rHref && (
                                        <EcoLink
                                            className="GtmModuleData-btn-common product-btn-amazon btn-amazon"
                                            href={data.rHref}
                                            data-btn-id={`product-btn-amazon-${k}`}
                                            dataLayer={{
                                                event: 'uaEvent',
                                                event_name: 'checkout',
                                                button_name: `${item?.lBtn}_${item.title}`,

                                                __btn_id1: `product-item-${k} .btn-amazon`,
                                                __btn_id2: `product-btn-amazon-${k}`,
                                                __btn_type: 'common',
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <EfIcon pc="https://websiteoss.ecoflow.com/upload/image/1721387747560/a2.png" />
                                            {item?.rBtn}
                                        </EcoLink>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
};
export default PowerSolution;
